/**
 * HapticsManager - thin wrapper around the Vibration API.
 *
 * Browser reality: navigator.vibrate is widely supported on Android but
 * unsupported on iOS Safari (Apple's choice) and no-op on desktop. We feature-
 * detect once at construction and silently no-op everywhere it's unavailable —
 * the API is never an error path even on missing platforms.
 *
 * Tuning: very short pulses (10-30ms) feel like a tap; longer pulses (80-150ms)
 * feel like an event. Patterns ([on, off, on, off]) ms feel like a buzz.
 *
 * Like SoundManager, mute state is persisted via the save schema's settings
 * slot (settings.hapticsEnabled). Defaults to enabled.
 */

import { load as loadSave, commit as commitSave } from '../persistence/saveState';

interface VibratingWindow extends Window {
  navigator: Navigator & { vibrate?: (pattern: number | number[]) => boolean };
}

class _HapticsManager {
  /** Cached at construction so we don't probe navigator every call. */
  private supported = false;
  private loaded = false;
  private _enabled = true;

  constructor() {
    if (typeof window !== 'undefined') {
      const w = window as VibratingWindow;
      this.supported = typeof w.navigator.vibrate === 'function';
    }
  }

  private ensureLoaded(): void {
    if (this.loaded) return;
    const save = loadSave();
    this._enabled = save.settings.hapticsEnabled !== false;
    this.loaded = true;
  }

  get enabled(): boolean {
    this.ensureLoaded();
    return this._enabled;
  }

  /** Does the current browser actually deliver haptics? */
  get isSupported(): boolean {
    return this.supported;
  }

  setEnabled(v: boolean): void {
    this.ensureLoaded();
    this._enabled = v;
    const save = loadSave();
    save.settings.hapticsEnabled = v;
    commitSave(save);
  }

  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  // --- Public play methods ----------------------------------------------

  /** Coconut pickup: short, crisp. */
  playCoconut(): void {
    this.vibrate(15);
  }

  /** Hidden coconut pickup: a quick double-tap, slightly more emphatic. */
  playInvisibleCoconut(): void {
    this.vibrate([12, 50, 25]);
  }

  /** Death: heavier, longer pulse. */
  playSplat(): void {
    this.vibrate(140);
  }

  /** Win: pattern that feels celebratory. */
  playWin(): void {
    this.vibrate([40, 60, 40, 60, 80]);
  }

  /** Adventure level cleared (not final). */
  playLevelClear(): void {
    this.vibrate([25, 50, 25]);
  }

  // --- Internals --------------------------------------------------------

  private vibrate(pattern: number | number[]): void {
    if (!this.enabled || !this.supported) return;
    try {
      (window as VibratingWindow).navigator.vibrate!(pattern);
    } catch {
      // Some browsers throw if the page is not focused; not worth surfacing.
    }
  }
}

export const HapticsManager = new _HapticsManager();
